// components/MultiPlatformPixelScript/googlePixel.ts — Google (gtag) pixel firing
import type { FireContext } from './types';
import { buildHashedUserDataForTikTokGoogle } from '../../utils/pixelPii';
import { makeEmbedSkipper } from '../../utils/embedTracking';
import { setGoogleUserData } from './googleUserData';
import { mapPlatformEvent } from './eventMaps';

export function fireGooglePixels(ctx: FireContext): void {
    const { configs, logPrefix, contentName, contentIds, orderData, value, userData, fireWithRetry, embedded, embedMode } = ctx;
    const skip = makeEmbedSkipper(embedded, embedMode, logPrefix);

    const googleConfigs = configs.filter(c => c.platform === 'google');
    if (googleConfigs.length === 0) return;
    if (typeof (window as any).gtag === 'undefined') {
        const firstId = googleConfigs[0].pixelIds[0];
        if (firstId) {
            const s = document.createElement('script');
            s.async = true;
            s.src = `https://www.googletagmanager.com/gtag/js?id=${firstId}`;
            document.head.appendChild(s);
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).gtag = function(){(window as any).dataLayer.push(arguments);}
            ;(window as any).gtag('js', new Date());
            console.log(`[${logPrefix} Google] Injected base SDK for ${firstId}`);
        }
    }

    if (!(window as any)._our_gtag_inited) {
        (window as any)._our_gtag_inited = new Set<string>();
    }

    const googleEventName = googleConfigs[0].eventName;
    // Hash DULU (await) baru config/event — user_data selalu ter-set sebelum event
    // (fix race: dulu event fire duluan tanpa user_data → Enhanced Conversions lemah).
    void (async () => {
        let hashed: Record<string, string> | null = null;
        try {
            if (userData && (userData.email || userData.phone || userData.name || userData.city || userData.province)) {
                hashed = await buildHashedUserDataForTikTokGoogle(userData);
            }
        } catch { hashed = null; }
        fireWithRetry(
            'Google',
            googleEventName,
            () => typeof (window as any).gtag === 'function',
            () => {
                const gtag = (window as any).gtag;
                if (hashed && Object.keys(hashed).length) setGoogleUserData(gtag, hashed, logPrefix);
                const ourGtagInited: Set<string> = (window as any)._our_gtag_inited || new Set();
                (window as any)._our_gtag_inited = ourGtagInited;

                const skipPageView = skip('Google', 'PageView');
                googleConfigs.forEach(config => {
                    config.pixelIds.filter(id => id?.trim()).forEach(pixelId => {
                        if (!ourGtagInited.has(pixelId)) {
                            ourGtagInited.add(pixelId);
                            if (skipPageView) {
                                gtag('config', pixelId, { send_page_view: false });
                                console.log(`[${logPrefix} Google] config (no auto page_view, embedded): ${pixelId}`);
                            } else {
                                gtag('config', pixelId);
                                console.log(`[${logPrefix} Google] config: ${pixelId}`);
                            }
                        } else {
                            console.log(`[${logPrefix} Google] skip config (already done): ${pixelId}`);
                        }
                    });
                });

                googleConfigs.forEach(config => {
                    if (skip('Google', config.eventName)) return;
                    config.pixelIds.filter(id => id?.trim()).forEach(pixelId => {
                        const effectiveValue = orderData?.value ?? value ?? 0;
                        const params: any = {
                            send_to: pixelId,
                            items: [{
                                item_id: orderData?.contentIds?.[0] || contentIds?.[0] || contentName || 'Form',
                                item_name: contentName || 'Form',
                                currency: orderData?.currency || 'IDR',
                                price: effectiveValue
                            }]
                        };

                        if (orderData) {
                            params.transaction_id = orderData.orderId;
                            params.value = orderData.value;
                        } else if (effectiveValue > 0) {
                            params.value = effectiveValue;
                        }

                        const googleEvent = mapPlatformEvent('google', config.eventName);

                        gtag('event', googleEvent, params);
                        console.log(`[${logPrefix} Google] ${googleEvent} FIRED for ${pixelId}!`);
                    });
                });
            }
        );
    })();
}
